import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Plus, X, BookOpen, Edit2, Award } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

// API ENDPOINTS
const API_BASE_URL = "https://backend.dlapakistan.com";
const API_ENDPOINTS = {
  GET: "/api/dla-fellowship/special-recognition/get/",
  CREATE: "/api/dla-fellowship/special-recognition/create/", 
  UPDATE: "/api/dla-fellowship/special-recognition/update/",
  DELETE: "/api/dla-fellowship/special-recognition/delete/"
};

const SpecialRecognitionAward = () => {
  const [awardsContents, setAwardsContents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  // SIRF DO FIELDS
  const [form, setForm] = useState({
    title: "",
    description: "",
    is_active: true
  });

  const editorRef = useRef(null);
  const emerald600 = "#059669";

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { "Content-Type": "application/json" },
    timeout: 10000,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const config = useMemo(() => ({
    readonly: false,
    height: 400,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Enter special recognition award description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  const fetchContents = async () => {
    try {
      setLoading(true);
      console.log("Fetching from:", API_BASE_URL + API_ENDPOINTS.GET);
      
      const response = await api.get(API_ENDPOINTS.GET);
      
      console.log("GET Response:", response.data);
      
      if (response.data && response.data.success) {
        if (response.data.data) {
          localStorage.setItem("specialRecognitionAward_Storage", JSON.stringify([response.data.data]));
          setAwardsContents([response.data.data]);
        } else {
          setAwardsContents([]);
          localStorage.removeItem("specialRecognitionAward_Storage");
        }
      }
    } catch (error) {
      console.error("GET Error:", error);
      const savedData = localStorage.getItem("specialRecognitionAward_Storage");
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setAwardsContents(parsedData);
        } catch (e) {
          console.error("Error parsing saved data:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setForm({ title: "", description: "", is_active: true });
    setEditorKey(prev => prev + 1);
  };

  const handleEditorChange = useCallback((newContent) => {
    setForm(prev => ({ ...prev, description: newContent }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim() || !form.description.trim()) {
      Swal.fire({ 
        icon: "warning", 
        title: "Missing Fields", 
        text: "Please fill in both title and description.", 
        confirmButtonColor: emerald600 
      });
      return;
    }

    try {
      setSaving(true);
      const dataToSend = { 
        title: form.title.trim(), 
        description: form.description.trim(),
        is_active: true 
      };
      
      let response;
      
      if (awardsContents.length === 0) {
        // CREATE
        console.log("Creating at:", API_BASE_URL + API_ENDPOINTS.CREATE);
        response = await api.post(API_ENDPOINTS.CREATE, dataToSend);
      } else {
        // UPDATE
        console.log("Updating at:", API_BASE_URL + API_ENDPOINTS.UPDATE);
        response = await api.put(API_ENDPOINTS.UPDATE, dataToSend);
      }
      
      console.log("Submit Response:", response.data);
      
      if (response.data && response.data.success) {
        await Swal.fire({ 
          icon: "success", 
          title: awardsContents.length === 0 ? "Created!" : "Updated!", 
          text: response.data.message,
          showConfirmButton: false, 
          timer: 1500 
        });
        
        await fetchContents();
        closeModal();
      } else {
        Swal.fire({ 
          icon: "error", 
          title: "Failed", 
          text: response.data?.message || "Something went wrong" 
        });
      }
      
    } catch (error) {
      console.error("Submit Error:", error);
      Swal.fire({ 
        icon: "error", 
        title: "Save Failed", 
        text: error.response?.data?.message || error.message || "An error occurred." 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will delete the Special Recognition Award content!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        console.log("Deleting at:", API_BASE_URL + API_ENDPOINTS.DELETE);
        const response = await api.delete(API_ENDPOINTS.DELETE);
        
        console.log("Delete Response:", response.data);
        
        if (response.data && response.data.success) {
          await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: response.data.message,
            timer: 1500,
            showConfirmButton: false
          });
          
          setAwardsContents([]);
          localStorage.removeItem("specialRecognitionAward_Storage");
          await fetchContents();
        }
      } catch (error) {
        console.error("Delete Error:", error);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: error.response?.data?.message || error.message
        });
      }
    }
  };

  const handleEdit = () => {
    if (awardsContents.length > 0) {
      const content = awardsContents[0];
      setForm({
        title: content.title || "",
        description: content.description || "",
        is_active: true
      });
      setShowModal(true);
    }
  };

  if (loading) return <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">DLA Special Recognition Award</h1>
            <p className="text-gray-600 mt-1">Manage Special Recognition Award page content</p>
          </div>
          
          <div className="flex gap-3">
            {awardsContents.length > 0 && (
              <button
                onClick={handleDelete}
                className="flex items-center px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium shadow-md hover:bg-red-700 transition-all"
              >
                <X className="w-4 h-4 mr-2" /> Delete
              </button>
            )}
            
            <button
              onClick={awardsContents.length === 0 ? () => setShowModal(true) : handleEdit}
              style={{ backgroundColor: emerald600 }}
              className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
            >
              {awardsContents.length === 0 ? <><Plus className="w-5 h-5 mr-2" /> Add</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>}
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 md:px-12 mt-16 w-full pb-20">
        {awardsContents.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">No Special Recognition Award content added yet.</p>
          </div>
        ) : (
          <div className="animate-fadeIn text-left w-full bg-transparent">
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">{awardsContents[0]?.title}</h2>
            </div>
            <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
              <div 
                className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent" 
                dangerouslySetInnerHTML={{ __html: awardsContents[0]?.description || "" }} 
              />
            </div>
          </div>
        )}
      </div>

      {/* MODAL - SIRF DO FIELDS */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">{awardsContents.length > 0 ? "Update Special Recognition Award" : "Create Special Recognition Award"}</h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Title</label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-600 transition-all" 
                    required 
                    disabled={saving} 
                    placeholder="Enter special recognition award title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Description</label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor 
                      key={`jodit-editor-${editorKey}`} 
                      ref={editorRef} 
                      value={form.description} 
                      config={config} 
                      onChange={handleEditorChange} 
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : awardsContents.length > 0 ? 'Update Special Recognition Award' : 'Create Special Recognition Award'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-modalIn { animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-description * { background-color: transparent !important; border: none !important; }
        .custom-description ul { list-style-type: disc; padding-left: 1.5rem; }
        .custom-description ol { list-style-type: decimal; padding-left: 1.5rem; }
        .jodit-editor-container :global(.jodit-container) { border: none !important; background: #f9fafb !important; }
      `}</style>
    </div>
  );
};

export default SpecialRecognitionAward;